import * as React from "react";
import { Link, useParams, useNavigate, Navigate } from "react-router-dom";
import Select, { createFilter } from 'react-select';
import { WebPortalClient, EcgAnalysisSetTest, SessionNote, TestNote, SessionAttachment, EcgTest } from "webportalclient";
import { SessionMetadata, OverreadStatusIdEnum, AnalysisSet } from "../misc/api/apiTypes";
import LoadingWithMessage from "../misc/components/LoadingWithMessage";
import { LeanSessionMetadata, LeanSessionMetadataTable } from "../PulmonaryFunctionTests/Shared/SessionMetadataTable";
import { ApiApplications } from "../misc/api/DeviceLinkApi/Applications";
import * as CalEcgApi from "../misc/api/DeviceLinkApi/CalEcg";
import { EcgTestAnnotations } from "./EcgTestAnnotations";
import DocumentTitle from "../misc/components/DocumentTitle";
import { SessionTypes } from "../misc/api/ApiSessionTypes";
import { EcgDashboard } from "./EcgDashboard";
import { PageValidatedAction, ToggleAlertFixedCommentCheckedAction, ToggleTestFixedCommentCheckedAction, useFixedComments } from "./UseFixedComments";
import { EcgCommentModals } from "./EcgCommentModals";
import { NotesModal } from "../misc/components/NotesModal";
import * as Moment from 'moment';
import { useSecurityContext } from "../Core/SecurityContext";
import AsyncActionButton from "../misc/components/AsyncActionButton";
import Alert, { AlertCategoryEnum } from "../misc/components/Alert";
import { useReferenceContextProvider } from "../Core/ReferenceContextProvider";
import { useCurrentStudyConfigurationContext } from "../Core/CurrentStudyConfigurationContext";
import { EcgAnalysisSetTypeEnum } from "../misc/api/ApiEcgAnalysisSetType";
import { CalculateHeartRate, CalculateQTcBazett, CalculateQTcFramingham, CalculateQTcFridericia, CalculateQTcHodges, DeriveParametersFromAnnotations } from "./EcgCalculations";
import { AnnotationDialog } from "./AnnotationDialogs";
import { EcgResultsPanel } from "./EcgResultsPanel";
import { PermissionEnum } from "../misc/api/ApiPermissionEnum";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../misc/api/WebPortalTokenClient";
import { Authorization } from "../misc/components/Authorization";
import FileAttachmentsTable from "../file-attachments/components/viewer/FileAttachmentsTable";
import { FileAttachmentsTableMode } from "../file-attachments/models";
import { AnnotatedEcgWrapper } from "webportalclient";
import { PerformanceSelect, PerformanceOption } from "../Components/PerformanceSelect";
import { ModalSize } from "../misc/components/modal";
import { GlasgowModal } from "./GlasgowModal";

export interface TestLeadAnnotations {
    ampsAnnotationsText: string;
    hl7AnnotationsText: string;
    annotations: CalEcgApi.LeadAnnotation[];
}

interface AnalysisButtons {
    analysis: boolean;
    analysisLoading: boolean;
    getAnnotations: boolean;
    close: boolean;
}

export const EcgAnalysisDetail = () => {
    const { analysisSetTestId } = useParams<{ analysisSetTestId: string }>();
    const { analyseMode } = useParams<{ analyseMode: string }>();
    const navigate = useNavigate();
    const client = useWebPortalApiClient();
    const legacyClient = useWebPortalLegacyClient();
    const { currentUser, currentUserPermissions } = useSecurityContext();
    const { overreadStatuses, countries } = useReferenceContextProvider();
    const { study } = useCurrentStudyConfigurationContext();
    const [sessionMetadata, setSessionMetadata] = React.useState<LeanSessionMetadata>();
    const [analysisSetTest, setAnalysisSetTest] = React.useState<EcgAnalysisSetTest>();
    const [testAnnotations, setTestAnnotations] = React.useState<TestLeadAnnotations>();
    const [analysisButtons, setAnalysisButtons] = React.useState<AnalysisButtons>();
    const [error, setError] = React.useState<string>();

    const [notes, setNotes] = React.useState<TestNote[]>([]);
    const [notesVisible, setNotesVisible] = React.useState(false);
    const [glasgowStatementsVisible, setGlasgowStatementsVisible] = React.useState(false);
    const [isAddingNewNote, setIsAddingNewNote] = React.useState(false);
    const [isSubmittingAccept, setIsSubmittingAccept] = React.useState(false);
    const [isSubmittingReject, setIsSubmittingReject] = React.useState(false);
    const [canEdit, setCanEdit] = React.useState(false);
    const [reviewAcceptance, setReviewAcceptance] = React.useState<boolean | undefined>();
    const [auditModalVisible, setAuditModalVisible] = React.useState<boolean>(false);
    const [attachments, setAttachments] = React.useState<SessionAttachment[]>([]);
    const [possibleComparisonRecordings, setPossibleComparisonRecordings] = React.useState<EcgTest[]>([]);
    const [selectedComparisonRecording, setSelectedComparisonRecording] = React.useState<EcgTest>();
    const [comparisonRecordingFullDetail, setComparisonRecordingFullDetail] = React.useState<EcgAnalysisSetTest>();
    const [comparisonNotes, setComparisonNotes] = React.useState<TestNote[]>();
    const [secondComparisonNotes, setSecondComparisonNotes] = React.useState<TestNote[]>();
    const [selectedSecondComparisonRecording, setSelectedSecondComparisonRecording] = React.useState<EcgTest>();
    const [secondComparisonRecordingFullDetail, setSecondComparisonRecordingFullDetail] = React.useState<EcgAnalysisSetTest>();

    const setDefaultButtons = (): AnalysisButtons => {
        return {
            analysis: true,
            analysisLoading: false,
            getAnnotations: false,
            close: false
        };
    }

    const [fixedCommentsState, fixedCommentsDispatch] = useFixedComments(analysisSetTest ? [analysisSetTest] : [], SessionTypes.Ecg);

    React.useEffect(() => {
        const getData = async () => {
            const getComparisonRecordings = async (testId: string) => {
                const comparisonRecordings = await client.client.getEcgTests("id,session,index", `session/visit/subject/site/studyId eq ${study.id} and id ne ${testId}`, "session/visit/subject/number asc, session/name asc, index asc", "session($select=name;$expand=visit($select=name;$expand=subject($select=number;$expand=site($select=number))))");
                setPossibleComparisonRecordings(comparisonRecordings.value);
            }

            const ecgAnalysisSetTestResult = await client.client.getEcgAnalysisSetTest(analysisSetTestId, "id,test,testId,analysisSet,analysisSetId,analyst,analystId,ampsAnnotationsTextBase64,hl7AnnotationsTextBase64,annotations,intepretations,alerts", "test,analysisSet($select=id,type,sessionId,status)");

            if (ecgAnalysisSetTestResult.analystId !== null && ecgAnalysisSetTestResult.analystId !== undefined && (currentUser.Id !== ecgAnalysisSetTestResult.analystId && analyseMode === "true"))
                navigate("/unauthorized");

            if ((analyseMode === "true" && (ecgAnalysisSetTestResult.analysisSet.type === EcgAnalysisSetTypeEnum.Overread || ecgAnalysisSetTestResult.analysisSet.type === EcgAnalysisSetTypeEnum.QC))
                || (analyseMode === "false" && ecgAnalysisSetTestResult.analysisSet.type === EcgAnalysisSetTypeEnum.Review)) {
                if (ecgAnalysisSetTestResult.analyst === null) {
                    await client.client.patchEcgAnalysisSet({
                        status: OverreadStatusIdEnum.Pending,
                        started: Moment().toISOString(),
                    }, ecgAnalysisSetTestResult.analysisSetId, "");

                    await client.client.patchEcgAnalysisSetTest({
                        analystId: currentUser.Id,
                    }, ecgAnalysisSetTestResult.id, "");
                }
            }

            if (ecgAnalysisSetTestResult.analyst !== null && ecgAnalysisSetTestResult.analyst !== currentUser.Email) {
                setCanEdit(false);
            }

            setAnalysisSetTest(ecgAnalysisSetTestResult);
            setTestAnnotations({
                ampsAnnotationsText: ecgAnalysisSetTestResult.ampsAnnotationsTextBase64,
                hl7AnnotationsText: ecgAnalysisSetTestResult.hl7AnnotationsTextBase64,
                annotations: ecgAnalysisSetTestResult.annotations.map(a => ({
                    annotation: a.annotation,
                    leadIndex: a.leadIndex,
                    beatIndex: a.beatIndex,
                    value: a.value
                }))
            });
            const specificSession = await client.client.getEcgSession(ecgAnalysisSetTestResult.analysisSet.sessionId, undefined, "visit($expand=subject($expand=site))");

            setAnalysisButtons(setDefaultButtons());

            const statusName = overreadStatuses.find(os => os.id === ecgAnalysisSetTestResult.analysisSet.status).name;

            setSessionMetadata({
                Id: specificSession.id,
                SiteId: specificSession.visit.subject.site.id,
                SiteName: specificSession.visit.subject.site.name,
                SiteCountryName: countries.find(c => c.id === specificSession.visit.subject.site.country)?.name,
                SiteCountryCode: specificSession.visit.subject.site.country,
                SiteNumber: specificSession.visit.subject.site.number,
                Technician: specificSession.technician,
                SubjectId: specificSession.visit.subject.id,
                SubjectNumber: specificSession.visit.subject.number,
                VisitName: specificSession.visit.name,
                SessionName: specificSession.name,
                SessionDateTime: specificSession.occurred,
                SessionTypeId: specificSession.sessionType,
                ReceivedDateTime: specificSession.created,
                SessionTypeName: specificSession.sessionType,
                Status: statusName,
                StatusId: ecgAnalysisSetTestResult.analysisSet.status,
                Overreader: ecgAnalysisSetTestResult.analyst,
                AnalysisSets: [ecgAnalysisSetTestResult.analysisSet].map(a => ({
                    Id: a.id,
                    Analyst: {
                        Username: ecgAnalysisSetTestResult.analyst
                    },
                    Status: {
                        Name: statusName
                    }
                }))
            });

            const attachmentsResults = await client.client.getSessionAttachments('id,size,name', `sessionId eq ${specificSession.id}`);
            setAttachments(attachmentsResults.value);

            for (let i = 0; i < ecgAnalysisSetTestResult.intepretations.length; i++) {
                fixedCommentsDispatch(ToggleTestFixedCommentCheckedAction.create(ecgAnalysisSetTestResult.testId, ecgAnalysisSetTestResult.intepretations[i].fixedCommentId, true));
            }

            for (let i = 0; i < ecgAnalysisSetTestResult.alerts.length; i++) {
                fixedCommentsDispatch(ToggleAlertFixedCommentCheckedAction.create(ecgAnalysisSetTestResult.testId, ecgAnalysisSetTestResult.alerts[i].fixedCommentId, true));
            }

            await fetchTestNotes(ecgAnalysisSetTestResult);
            getComparisonRecordings(ecgAnalysisSetTestResult.testId);
        }

        getData();

    }, [analysisSetTestId]);

    React.useEffect(() => {
        document.getElementsByClassName('alert-danger')[0]?.scrollIntoView({ behavior: 'smooth' });
    }, [error]);

    React.useEffect(() => {
        const getFullDetail = async () => {
            if (selectedComparisonRecording) {
                const comparisonRecording = await client.client.getEcgAnalysisSetTests("id,hl7AnnotationsTextBase64,ampsAnnotationsTextBase64,intepretations,alerts", `testId eq ${selectedComparisonRecording.id} and analysisSet/type eq '${EcgAnalysisSetTypeEnum.Overread}'`);
                setComparisonRecordingFullDetail(comparisonRecording.value[0]);

                const comparisonNotesResult = await client.client.getTestNotes(undefined, `testId eq ${selectedComparisonRecording.id}`);
                setComparisonNotes(comparisonNotesResult.value);
            }
        }

        getFullDetail();
    }, [selectedComparisonRecording]);

    React.useEffect(() => {
        const getFullDetail = async () => {
            if (selectedSecondComparisonRecording) {
                const comparisonRecording = await client.client.getEcgAnalysisSetTests("id,hl7AnnotationsTextBase64,ampsAnnotationsTextBase64,intepretations,alerts", `testId eq ${selectedSecondComparisonRecording.id} and analysisSet/type eq '${EcgAnalysisSetTypeEnum.Overread}'`);
                setSecondComparisonRecordingFullDetail(comparisonRecording.value[0]);

                const secondComparisonNotesResult = await client.client.getTestNotes(undefined, `testId eq ${selectedSecondComparisonRecording.id}`);
                setSecondComparisonNotes(secondComparisonNotesResult.value);
            }
        }

        getFullDetail();
    }, [selectedSecondComparisonRecording]);

    React.useEffect(() => {
        if (analyseMode === "true" && (analysisSetTest?.analysisSet?.type === EcgAnalysisSetTypeEnum.Overread || analysisSetTest?.analysisSet?.type === EcgAnalysisSetTypeEnum.QC)
            && (fixedCommentsState.testFixedComments[0]?.comments?.length > 0 && fixedCommentsState.alertFixedComments[0]?.comments?.length > 0)) {
            setCanEdit(true);
        }
    }, [analysisSetTest, analyseMode, fixedCommentsState.testFixedComments, fixedCommentsState.alertFixedComments]);

    const onAnalyse = async (testId: string, comparisonTest: EcgTest, secondComparisonTest: EcgTest) => {
        setAnalysisButtons({
            ...analysisButtons,
            analysisLoading: true,
        });

        await openCalEcg(testId, comparisonTest, secondComparisonTest);
    }

    const openCalEcg = async (testId: string, comparisonTest: EcgTest, secondComparisonTest: EcgTest) => {
        setError(undefined);

        try {
            const apps = await ApiApplications();
            const calECGApp = apps.find(x => x.name === "CalECG");
            await CalEcgApi.CreateInstance(calECGApp.id);

            if (comparisonTest) {
                let comparisonAnnotationEcgWrapper: AnnotatedEcgWrapper = { text: undefined };
                if (!comparisonRecordingFullDetail.hl7AnnotationsTextBase64) {
                    comparisonAnnotationEcgWrapper = await client.client.annotatedEcg({
                        testId: comparisonTest.id,
                    });
                } else {
                    comparisonAnnotationEcgWrapper.text = comparisonRecordingFullDetail.hl7AnnotationsTextBase64;
                }

                await CalEcgApi.CreateComparisonInstance(calECGApp.id);
                await CalEcgApi.ComparisonProtocol(calECGApp.id);
                await CalEcgApi.OpenComparisonEcg(calECGApp.id, comparisonAnnotationEcgWrapper.text);

                if (!comparisonRecordingFullDetail.hl7AnnotationsTextBase64 && comparisonRecordingFullDetail.ampsAnnotationsTextBase64) {
                    await CalEcgApi.ImportComparisonAnnotationsTextFile(calECGApp.id, comparisonRecordingFullDetail.ampsAnnotationsTextBase64);
                }

                await CalEcgApi.SetComparisonTitle(calECGApp.id, `${comparisonTest.session.visit.subject.site.number} - ${comparisonTest.session.visit.subject.number} - ${comparisonTest.session.name} - ${comparisonTest.index}`);
                await CalEcgApi.SetComparisonPermission(calECGApp.id, CalEcgApi.CalEcgPermissions.All, false);
                await CalEcgApi.SetComparisonPermission(calECGApp.id, CalEcgApi.CalEcgPermissions.ViewDemographics, true);
                await CalEcgApi.ShowComparisonWindow(calECGApp.id, true);
            }

            if (secondComparisonTest) {
                let secondComparisonAnnotationEcgWrapper: AnnotatedEcgWrapper = { text: undefined };
                if (!secondComparisonRecordingFullDetail.hl7AnnotationsTextBase64) {
                    secondComparisonAnnotationEcgWrapper = await client.client.annotatedEcg({
                        testId: comparisonTest.id,
                    });
                } else {
                    secondComparisonAnnotationEcgWrapper.text = secondComparisonRecordingFullDetail.hl7AnnotationsTextBase64;
                }

                await CalEcgApi.CreateSecondComparisonInstance(calECGApp.id);
                await CalEcgApi.SecondComparisonProtocol(calECGApp.id);
                await CalEcgApi.OpenSecondComparisonEcg(calECGApp.id, secondComparisonAnnotationEcgWrapper.text);

                if (!secondComparisonRecordingFullDetail.hl7AnnotationsTextBase64 && secondComparisonRecordingFullDetail.ampsAnnotationsTextBase64) {
                    await CalEcgApi.ImportSecondComparisonAnnotationsTextFile(calECGApp.id, secondComparisonRecordingFullDetail.ampsAnnotationsTextBase64);
                }

                await CalEcgApi.SetSecondComparisonTitle(calECGApp.id, `${secondComparisonTest.session.visit.subject.site.number} - ${secondComparisonTest.session.visit.subject.number} - ${secondComparisonTest.session.name} - ${secondComparisonTest.index}`);
                await CalEcgApi.SetSecondComparisonPermission(calECGApp.id, CalEcgApi.CalEcgPermissions.All, false);
                await CalEcgApi.SetSecondComparisonPermission(calECGApp.id, CalEcgApi.CalEcgPermissions.ViewDemographics, true);
                await CalEcgApi.ShowSecondComparisonWindow(calECGApp.id, true);
            }

            await CalEcgApi.Protocol(calECGApp.id);

            if (!analysisSetTest.hl7AnnotationsTextBase64) {
                const annotationEcgWrapper = await client.client.annotatedEcg({
                    testId: testId,
                });
                const ecgOpenResult = await CalEcgApi.OpenEcg(calECGApp.id, annotationEcgWrapper.text);

                if (ecgOpenResult !== CalEcgApi.EcgFileOpenResult.RhythmOnly && ecgOpenResult !== CalEcgApi.EcgFileOpenResult.RepresentativeBeat) {
                    setError(`Failed to launch CalECG with the given file, response: ${ecgOpenResult}`);
                    return;
                }
            } else {
                const ecgOpenResult = await CalEcgApi.OpenEcg(calECGApp.id, analysisSetTest.hl7AnnotationsTextBase64);

                if (ecgOpenResult !== CalEcgApi.EcgFileOpenResult.RhythmOnly && ecgOpenResult !== CalEcgApi.EcgFileOpenResult.RepresentativeBeat) {
                    setError(`Failed to launch CalECG with the given file, response: ${ecgOpenResult}`);
                    return;
                }
            }

            if (!analysisSetTest.hl7AnnotationsTextBase64 && testAnnotations.ampsAnnotationsText) {
                await CalEcgApi.ImportAnnotationsTextFile(calECGApp.id, testAnnotations.ampsAnnotationsText);
            }

            if (analysisSetTest.analysisSet.type !== EcgAnalysisSetTypeEnum.QC)
                await CalEcgApi.SetTitle(calECGApp.id, `${sessionMetadata.SiteNumber} - ${sessionMetadata.SubjectNumber} - ${sessionMetadata.SessionName} - ${analysisSetTest.test.index}`);
            else
                await CalEcgApi.SetTitle(calECGApp.id, `QC recording ${analysisSetTest.test.index}`);

            if (canEdit) {
                await CalEcgApi.SetPermission(calECGApp.id, CalEcgApi.CalEcgPermissions.All, true);
            } else {
                await CalEcgApi.SetPermission(calECGApp.id, CalEcgApi.CalEcgPermissions.All, false);
                await CalEcgApi.SetPermission(calECGApp.id, CalEcgApi.CalEcgPermissions.ViewDemographics, true);
            }

            if (analysisSetTest.analysisSet.type === EcgAnalysisSetTypeEnum.QC)
                await CalEcgApi.SetPermission(calECGApp.id, CalEcgApi.CalEcgPermissions.ViewDemographics, false);

            await CalEcgApi.SetDigitalDefaults(calECGApp.id);

            setAnalysisButtons({
                analysis: false,
                analysisLoading: false,
                getAnnotations: true,
                close: true,
            });

            await CalEcgApi.ShowWindow(calECGApp.id, true);
        } catch {
            setError("Failed to launch CalECG please ensure CalECG and Vitalograph Device Link are installed.");
            setAnalysisButtons(setDefaultButtons());
        }
    }

    const onGetAnnotations = async () => {
        const apps = await ApiApplications();
        const calECGApp = apps.find(x => x.name === "CalECG");

        const result = await CalEcgApi.GetAnnotations(calECGApp.id);
        const annotationFileResult = await CalEcgApi.ExportEcgToBase64(calECGApp.id, 3);
        const hl7AnnotationFileResult = await CalEcgApi.ExportEcgToBase64(calECGApp.id, 1);

        setTestAnnotations({
            ampsAnnotationsText: annotationFileResult,
            hl7AnnotationsText: hl7AnnotationFileResult,
            annotations: result,
        });
    }

    const onCloseCalEcg = async () => {
        try {
            const apps = await ApiApplications();
            const calECGApp = apps.find(x => x.name === "CalECG");

            if (calECGApp.isLaunched)
                await CalEcgApi.CloseApplication(calECGApp.id);
        } catch {
            setError("Failed to launch CalECG please ensure CalECG and Vitalograph Device Link are installed.");
        }

        setAnalysisButtons(setDefaultButtons());
    }

    const onComplete = async (analysisSetTestId: string, testId: string, auditAnnotation: string = "") => {
        setError(undefined);
        setAuditModalVisible(false);
        setIsSubmittingAccept(true);

        const interpretations = fixedCommentsState.testFixedComments.find(x => x.id === testId);
        const commentsLength = interpretations?.comments.filter(c => c.selected).length;

        if (commentsLength === undefined || commentsLength === 0) {
            setError("At least one interpretation must be selected.");
            setIsSubmittingAccept(false);
            return;
        }

        const { rr, qt } = DeriveParametersFromAnnotations(testAnnotations.annotations);

        const heartRate = CalculateHeartRate(rr);

        await client.client.patchEcgAnalysisSetTest({
            accepted: true,
            analysisSetId: analysisSetTest.analysisSetId,
            testId: testId,
            ampsAnnotationsTextBase64: testAnnotations.ampsAnnotationsText,
            hl7AnnotationsTextBase64: testAnnotations.hl7AnnotationsText,
            rr: rr,
            hr: heartRate,
            qTcBaz: CalculateQTcBazett(qt, rr),
            qTcFri: CalculateQTcFridericia(qt, rr),
            qTcFra: CalculateQTcFramingham(qt, rr),
            qTcHod: CalculateQTcHodges(qt, heartRate),
            analysisCompleted: Moment().toISOString(true),
            annotations: testAnnotations.annotations.map(a => ({
                annotation: a.annotation,
                beatIndex: a.beatIndex,
                leadIndex: a.leadIndex,
                value: a.value
            })),
            intepretations: interpretations.comments.filter(c => c.selected).map(c => ({
                analysisSetTestId: analysisSetTestId,
                fixedCommentId: c.id,
            })),
            alerts: fixedCommentsState.alertFixedComments.find(x => x.id === testId).comments.filter(c => c.selected).map(c => ({
                analysisSetTestId: analysisSetTestId,
                fixedCommentId: c.id,
            }))
        }, analysisSetTestId, auditAnnotation);

        const allTests = await client.client.getEcgAnalysisSetTests(undefined, `analysisSetId eq ${analysisSetTest.analysisSetId}`, undefined);
        if (allTests.value.every(x => x.accepted)) {
            await client.client.patchEcgAnalysisSet({
                status: OverreadStatusIdEnum.Accepted,
            }, analysisSetTest.analysisSetId, auditAnnotation);
        }

        await onCloseCalEcg();

        setIsSubmittingAccept(false);
        navigate("/ecg/analysis", { state: { refresh: true } });
    }

    const onReview = async (analysisSetTest: EcgAnalysisSetTest, accepted: boolean) => {
        accepted ? setIsSubmittingAccept(true) : setIsSubmittingReject(true);
        setReviewAcceptance(accepted);
        if (!accepted || (analysisSetTest.accepted !== null && analysisSetTest.accepted !== accepted)) {
            setAuditModalVisible(true);
            setIsSubmittingAccept(false);
            setIsSubmittingReject(false);
            return;
        }

        await onReviewComplete(analysisSetTestId, accepted);
        await onCloseCalEcg();
        setIsSubmittingAccept(false);
        setIsSubmittingReject(false);
    }

    const onReviewComplete = async (analysisSetTestId: string, accepted: boolean, auditAnnotation: string = null) => {
        setError(undefined);
        setReviewAcceptance(undefined);
        setAuditModalVisible(false);

        await client.client.patchEcgAnalysisSetTest({
            accepted: accepted,
            analysisCompleted: Moment().toISOString(true),
        }, analysisSetTestId, auditAnnotation);

        const allTests = await client.client.getEcgAnalysisSetTests(undefined, `analysisSetId eq ${analysisSetTest.analysisSetId}`, undefined);
        if (allTests.value.every(x => x.accepted)) {
            await client.client.patchEcgAnalysisSet({
                status: OverreadStatusIdEnum.Accepted,
            }, analysisSetTest.analysisSetId, "");
        }

        setIsSubmittingAccept(false);
        setIsSubmittingReject(false);
        navigate("/ecg/analysis", { state: { refresh: true } });
    }

    const onInitialComplete = async (analysisSetTest: EcgAnalysisSetTest, testId: string) => {
        const selectedFixedComments = fixedCommentsState.testFixedComments.find(x => x.id === testId).comments.filter(x => x.selected).map(x => x.id);
        if (selectedFixedComments.filter(c => !analysisSetTest.intepretations.map(x => x.fixedCommentId).includes(c)).length > 0) {
            setAuditModalVisible(true);
            return;
        }

        const selectedAlertFixedComments = fixedCommentsState.alertFixedComments.find(x => x.id === testId).comments.filter(x => x.selected).map(x => x.id);
        if (selectedAlertFixedComments.filter(c => !analysisSetTest.alerts.map(x => x.fixedCommentId).includes(c)).length > 0) {
            setAuditModalVisible(true);
            return;
        }

        if (testAnnotations.hl7AnnotationsText != analysisSetTest.hl7AnnotationsTextBase64) {
            setAuditModalVisible(true);
            return;
        }

        onComplete(analysisSetTest.id, testId, "");
    }

    const fetchTestNotes = async (analysisSetTest: EcgAnalysisSetTest) => {
        if (analysisSetTest.analysisSet.type !== EcgAnalysisSetTypeEnum.QC) {
            const notesResult = await client.client.getTestNotes(undefined, `testId eq ${analysisSetTest.testId}`);
            const sessionNotesResult = await client.client.getSessionNotes(undefined, `sessionId eq ${analysisSetTest.analysisSet.sessionId}`);
            setNotes([
                ...notesResult.value,
                ...sessionNotesResult.value
            ].sort((a, b) => Moment(a.recorded).diff(Moment(b.recorded))));
        } else {
            setNotes([]);
        }
    }

    const addNewNote = async (comment: string) => {
        await client.client.postTestNote(
            {
                testId: analysisSetTest.testId,
                userEmailAddress: currentUser.Email,
                comment: comment,
                recorded: Moment().toISOString()
            })
        await fetchTestNotes(analysisSetTest);
    }

    const editTestNote = async (id: string, comment: string, annotation: string) => {
        await client.client.patchTestNote(
            {
                comment: comment,
            }, id, annotation)
        await fetchTestNotes(analysisSetTest);
    }


    if (!(sessionMetadata && analysisButtons && analysisSetTest && fixedCommentsState.areAvailableFixedCommentsLoaded !== null))
        return <LoadingWithMessage message="Loading ECG Recording..." />;

    const hasAddingNotePermission = currentUserPermissions.some(x => x.Id == PermissionEnum.AddEcgSessionNotes);
    const hasViewNotePermission = currentUserPermissions.some(x => x.Id == PermissionEnum.ViewEcgSessionNotes);

    const analysisDisabled = (fixedCommentsState.areAvailableFixedCommentsLoaded === false ||
        (fixedCommentsState?.testFixedComments[0]?.comments?.length === 0 || fixedCommentsState.alertFixedComments[0]?.comments?.length === 0
            || fixedCommentsState?.testFixedComments[0]?.comments?.length === undefined || fixedCommentsState.alertFixedComments[0]?.comments?.length === undefined));

    const isQcAnalysisSet = analysisSetTest.analysisSet.type === EcgAnalysisSetTypeEnum.QC;
    const isUnauthorized = isQcAnalysisSet && analysisSetTest.analyst !== null && analysisSetTest.analystId !== currentUser.Id;

    if (isUnauthorized) {
        return <Navigate to={{ pathname: "/unauthorized" }} />;
    }

    const comparisonLabel = (o: EcgTest) => `${o.session.visit.subject.site.number} - ${o.session.visit.subject.number} - ${o.session.name} - ${o.index}`;

    const actionControls = <>
        <tr>
            <td><b>Analysis</b></td>
            <td>
                <AsyncActionButton
                    disabled={!analysisButtons.analysis}
                    isSubmitting={analysisButtons.analysisLoading}
                    className="btn btn-primary"
                    onClick={() => onAnalyse(analysisSetTest.testId, selectedComparisonRecording, selectedSecondComparisonRecording)}
                >
                    Open CalECG
                </AsyncActionButton>&nbsp;
                <button disabled={!analysisButtons.getAnnotations || !canEdit} className="btn btn-primary" onClick={() => onGetAnnotations()}>Sync Annotations</button>&nbsp;
                <button disabled={!analysisButtons.close} className="btn btn-primary" onClick={() => onCloseCalEcg()}>Close</button>
            </td>
        </tr>
        {analysisSetTest.analysisSet.type !== EcgAnalysisSetTypeEnum.QC ? (<><tr>
            <td><b>Compare with</b></td>
            <td>
                <Select
                    isClearable
                    components={{ MenuList: PerformanceSelect, Option: PerformanceOption }}
                    options={possibleComparisonRecordings}
                    getOptionLabel={o => comparisonLabel(o)}
                    getOptionValue={o => o.id}
                    isMulti={false}
                    filterOption={createFilter({ ignoreAccents: false })}
                    onChange={(e: EcgTest) => { setSelectedComparisonRecording(e); setComparisonRecordingFullDetail(undefined); }}
                />
            </td>
        </tr>
            <tr>
                <td><b>Also Compare with</b></td>
                <td>
                    <Select
                        isClearable
                        components={{ MenuList: PerformanceSelect, Option: PerformanceOption }}
                        options={possibleComparisonRecordings}
                        getOptionLabel={o => comparisonLabel(o)}
                        getOptionValue={o => o.id}
                        isMulti={false}
                        filterOption={createFilter({ ignoreAccents: false })}
                        onChange={(e: EcgTest) => { setSelectedSecondComparisonRecording(e); setSecondComparisonRecordingFullDetail(undefined); }}
                    />
                </td>
            </tr></>) : null}
    </>

    const comparisonTestFixedComments = [
        selectedComparisonRecording && comparisonRecordingFullDetail ? { title: comparisonLabel(selectedComparisonRecording), items: comparisonRecordingFullDetail.intepretations } : undefined,
        selectedSecondComparisonRecording && secondComparisonRecordingFullDetail ? { title: comparisonLabel(selectedSecondComparisonRecording), items: secondComparisonRecordingFullDetail.intepretations } : undefined
    ].filter(x => x);

    const comparisonAlertFixedComments = [
        selectedComparisonRecording && comparisonRecordingFullDetail ? { title: comparisonLabel(selectedComparisonRecording), items: comparisonRecordingFullDetail.alerts } : undefined,
        selectedSecondComparisonRecording && secondComparisonRecordingFullDetail ? { title: comparisonLabel(selectedSecondComparisonRecording), items: secondComparisonRecordingFullDetail.alerts } : undefined
    ].filter(x => x);

    const combinedComparisonNotes = [
        selectedComparisonRecording && comparisonNotes ? { title: comparisonLabel(selectedComparisonRecording), items: comparisonNotes } : undefined,
        selectedSecondComparisonRecording && secondComparisonNotes ? { title: comparisonLabel(selectedSecondComparisonRecording), items: secondComparisonNotes } : undefined,
    ].filter(x => x);

    return (
        <React.StrictMode>
            <DocumentTitle title="ECG Analysis | Vitalograph Web Portal">
                <Alert category={AlertCategoryEnum.Danger} show={error !== undefined}>
                    {error}
                </Alert>
                <Alert show={analysisDisabled} category={AlertCategoryEnum.Danger}>
                    Analysis disabled. Interpretations and Alerts have not been configured for this study, please ask the Administrator to configure these first.
                </Alert>
                <div className="row">
                    <div className="col-md-6">
                        <h2 className="text-primary">ECG Recording</h2>
                    </div>
                    <div className="h2 col-md-6">
                        <Link to="/ecg/analysis" className="pull-right btn btn-outline btn-primary" onClick={() => onCloseCalEcg()}>Back to List</Link>
                    </div>
                </div>
                {analysisSetTest.analysisSet.type !== EcgAnalysisSetTypeEnum.QC ? <LeanSessionMetadataTable
                    sessionMetadata={sessionMetadata}
                    selectedAnalysisSetId={analysisSetTest.analysisSetId}
                /> : null}

                <h2 className="text-primary">Results</h2>
                <React.Fragment key={`ResultPanel${analysisSetTest.id}`}>
                    <EcgResultsPanel test={analysisSetTest.test} controls={actionControls} isQC={analysisSetTest.analysisSet.type === EcgAnalysisSetTypeEnum.QC}>
                        <EcgTestAnnotations testAnnotations={testAnnotations?.annotations} />
                    </EcgResultsPanel>

                    <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionId={PermissionEnum.AccessEcgAnalysisSetsAttachment}>
                        <h2 className="text-primary">Attachments</h2>
                        <div className="panel panel-default">
                            <div className="panel-body">
                                <FileAttachmentsTable
                                    legacyClient={legacyClient}
                                    fileAttachments={attachments.map(a => ({ id: a.id, fileSizeInBytes: a.size, fileName: a.name }))}
                                    mode={FileAttachmentsTableMode.ListOnly}
                                />
                            </div>
                        </div>
                    </Authorization>

                    <EcgDashboard
                        test={analysisSetTest}
                        isAnalysis={analysisSetTest.analysisSet.type === EcgAnalysisSetTypeEnum.Overread || analysisSetTest.analysisSet.type === EcgAnalysisSetTypeEnum.QC}
                        canEdit={canEdit}
                        fixedCommentsDispatch={fixedCommentsDispatch}
                        fixedCommentsState={fixedCommentsState}
                        setNotesVisible={(v) => setNotesVisible(v)}
                        setGlasgowStatementsVisible={(v) => setGlasgowStatementsVisible(v)}
                        notes={notes}
                        isSubmittingAccept={isSubmittingAccept}
                        isSubmittingReject={isSubmittingReject}
                        canViewNotes={hasViewNotePermission}
                        canAddNotes={hasAddingNotePermission}
                        onComplete={() => onInitialComplete(analysisSetTest, analysisSetTest.testId)}
                        onReview={(accepted) => onReview(analysisSetTest, accepted)}
                    />
                </React.Fragment>

                <EcgCommentModals
                    editable={canEdit}
                    testFixedComments={fixedCommentsState.testFixedComments}
                    alertFixedComments={fixedCommentsState.alertFixedComments}
                    testComparisonFixedComments={comparisonTestFixedComments}
                    alertComparisonFixedComments={comparisonAlertFixedComments}
                    dispatch={fixedCommentsDispatch}
                />

                {glasgowStatementsVisible ? <GlasgowModal test={analysisSetTest.test} onClose={() => setGlasgowStatementsVisible(false)} /> : null}

                {hasViewNotePermission ? <NotesModal
                    isVisible={notesVisible}
                    header="Recording Notes"
                    isAddingNote={isAddingNewNote}
                    notes={notes}
                    comparisonNotes={combinedComparisonNotes}
                    comparisonColumnHeader="Recording"
                    onAdd={annotation => addNewNote(annotation)}
                    // onEdit={(id, comment, annotation) => editTestNote(id, comment, annotation)}
                    onCloseModal={() => setNotesVisible(false)}
                    disabled={!canEdit || analysisSetTest.analysisSet.type != EcgAnalysisSetTypeEnum.Overread || !hasAddingNotePermission}
                    modalSize={ModalSize.Large}
                /> : null}
                {auditModalVisible ? <AnnotationDialog
                    header={reviewAcceptance === false ? "Disagree with ECG Analysis" : "Approval"}
                    content={reviewAcceptance === false ? "Please enter a comment to notify the analyst why you are disagreeing with the analysis of this ECG recording" : null}
                    onClose={() => { setAuditModalVisible(false); setReviewAcceptance(undefined); }}
                    onSubmit={(audit) => {
                        if (analysisSetTest.analysisSet.type === EcgAnalysisSetTypeEnum.Review)
                            return onReviewComplete(analysisSetTest.id, reviewAcceptance, audit.Annotation);
                        else
                            return onComplete(analysisSetTest.id, analysisSetTest.testId, audit.Annotation);

                    }}
                /> : null}
            </DocumentTitle>
        </React.StrictMode>
    );
}
